import { Component, Input, Output, EventEmitter, ContentChild, ElementRef, Renderer2, AfterContentInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ICONS } from '../../../_helpers/icons.constants';
import { DEFAULT_MODAL_CONFIG, ModalConfig } from './modal.modal';

@Component({
  selector: 'ds-modal',
  templateUrl: './modal.component.html',
  standalone: true,
  imports: [CommonModule]
})
export class ModalComponent implements AfterContentInit, OnChanges, OnDestroy {
  public ICONS = ICONS;
  @Output() visibleChange = new EventEmitter<boolean>();

  private originalParent: Node | null = null;
  private originalNextSibling: Node | null = null;
  private movedToBody = false;

  constructor(private elementRef: ElementRef<HTMLElement>, private renderer: Renderer2) {}


  // Emit when the modal is confirmed (e.g. Save/OK)
  @Output() confirmed = new EventEmitter<any>();

  // Content projection detection
  @ContentChild('[modal-header]', { read: ElementRef }) projectedHeader?: ElementRef;
  @ContentChild('[modal-footer]', { read: ElementRef }) projectedFooter?: ElementRef;

  public _modalInput: ModalConfig = { ...DEFAULT_MODAL_CONFIG };
  @Input() modalInput!: ModalConfig;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.modalInput) {
      const val: ModalConfig = changes.modalInput.currentValue || {};
      this.modalInput = { ...DEFAULT_MODAL_CONFIG, ...val };
    }
    if (this.modalInput?.visible) {
      this.moveToBody();
    } else {
      this.restorePosition();
    }
  }

  ngOnDestroy(): void {
    if (this.movedToBody) {
      const host = this.elementRef.nativeElement;
      host.parentNode?.removeChild(host);
      this.movedToBody = false;
    }
  }

  private moveToBody(): void {
    if (this.movedToBody) { return; }
    const host = this.elementRef.nativeElement;
    this.originalParent = host.parentNode;
    this.originalNextSibling = host.nextSibling;
    this.renderer.appendChild(document.body, host);
    this.movedToBody = true;
  }

  private restorePosition(): void {
    if (!this.movedToBody) { return; }
    const host = this.elementRef.nativeElement;
    if (this.originalParent) {
      this.renderer.insertBefore(this.originalParent, host, this.originalNextSibling);
    }
    this.movedToBody = false;
  }

  hasProjectedHeader = false;
  hasProjectedFooter = false;

  ngAfterContentInit(): void {
    this.hasProjectedHeader = !!this.projectedHeader;
    this.hasProjectedFooter = !!this.projectedFooter;
  }

  // Returns the header icon path (if any) depending on the variant
  get headerIcon(): string | null {
    switch (this.modalInput?.variant) {
      case 'error':
        return this.ICONS.ALERT_CIRCLE;
      case 'success':
        return this.ICONS.CHECK_CIRCLE;
      case 'warning':
        return this.ICONS.WARNING;
      default:
        return null;
    }
  }

  get headerIconAlt(): string {
    switch (this.modalInput?.variant) {
      case 'error':
        return 'Error';
      case 'success':
        return 'Success';
      case 'warning':
        return 'Warning';
      default:
        return '';
    }
  }

  open(data?: any) {
    if (data !== undefined) this.modalInput.data = data;
    this.modalInput.visible = true;
    this.visibleChange.emit(true);
  }

  close() {
    this.modalInput.visible = false;
    this.visibleChange.emit(false);
  }

  confirm(payload?: any) {
    this.confirmed.emit(payload !== undefined ? payload : this.modalInput.data);
    this.close();
  }
}
